import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { ColumnDef, Table, Row } from "@tanstack/react-table";
import { MoreHorizontal, Pencil, Trash2, UserCheck } from "lucide-react";
import { Application } from "../../applications/types";
import { Profile } from "../../talent/types";
import { COUNTRY_OPTIONS } from "../../applications/constants/countries";
import { getLanguageLevelIcon } from "../../applications/utils/languageUtils";
import { getPhaseIndex } from "../../applications/utils/phaseProgressUtils";
import { getApplicationPhase } from "../../applications/utils/phaseUtils";
import { getQualificationType } from "../../applications/utils/qualificationUtils";
import { getStatusLabel, cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export type ApplicationOrProfile = Application | Profile;

export const getColumnDefinitions = (
  isApplicationList: boolean,
  avatarUrls: Record<string, string>,
  canShowActions: boolean,
  canImpersonateProfiles: boolean,
  t: any,
  onEditTalent: (row: any) => void,
  onImpersonate: (id: string) => void,
  onDelete: (id: string) => void
): ColumnDef<ApplicationOrProfile>[] => {
  return [
    // Select column
    ...(isApplicationList
      ? [
          {
            id: "select",
            header: ({ table }: { table: Table<ApplicationOrProfile> }) => (
              <Checkbox
                checked={table.getIsAllPageRowsSelected()}
                onCheckedChange={(value) =>
                  table.toggleAllPageRowsSelected(!!value)
                }
                aria-label={t("actions.select_all")}
              />
            ),
            cell: ({ row }: { row: any }) => (
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label={t("actions.select_row")}
              />
            ),
            enableSorting: false,
            enableHiding: false
          }
        ]
      : []),
    {
      id: "name",
      header: t("columns.name"),
      accessorFn: (row) =>
        `${row.given_name || ""} ${row.family_name || ""}`.trim(),
      enableSorting: true,
      cell: ({ row }) => {
        const givenName = row.original.given_name;
        const familyName = row.original.family_name;
        const id = isApplicationList
          ? (row.original as Application).talent_application
          : (row.original as Profile).talent_id;
        const avatarUrl = id ? avatarUrls[id] : undefined;

        return (
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarImage src={avatarUrl} />
              <AvatarFallback>{`${givenName?.[0] || ""}${familyName?.[0] || ""}`}</AvatarFallback>
            </Avatar>
            <span>{`${givenName || ""} ${familyName || ""}`}</span>
          </div>
        );
      }
    },
    // Job title column
    ...(isApplicationList
      ? [
          {
            id: "job_title",
            header: t("columns.job_title"),
            accessorFn: (row: ApplicationOrProfile) =>
              isApplicationList
                ? (row as Application).job_title || t("messages.not_available")
                : t("messages.not_available"),
            enableSorting: true,
            cell: ({ row }: { row: Row<ApplicationOrProfile> }) => {
              const jobTitle = isApplicationList
                ? (row.original as Application).job_title
                : null;
              return (
                <div className="max-w-[200px]">
                  <span className="text-sm font-medium text-gray-900 truncate block">
                    {jobTitle || t("messages.not_available")}
                  </span>
                </div>
              );
            }
          }
        ]
      : []),
    {
      id: "qualification",
      header: t("columns.qualification"),
      accessorFn: (row) => getQualificationType(row),
      enableSorting: true,
      cell: ({ row }) => {
        const qualification = getQualificationType(row.original);
        return (
          <Badge variant="outline" className="bg-gray-100 border-gray-200">
            {qualification}
          </Badge>
        );
      }
    },
    // Phase column
    ...(isApplicationList
      ? [
          {
            id: "phase",
            header: t("columns.phase"),
            accessorFn: (row: ApplicationOrProfile) =>
              isApplicationList
                ? getPhaseIndex(getApplicationPhase(row as Application))
                : 0,
            enableSorting: true,
            cell: ({ row }: { row: Row<ApplicationOrProfile> }) => {
              if (!isApplicationList) return "-";
              const phase = getApplicationPhase(row.original as Application);
              const phaseIndex = getPhaseIndex(phase);
              const circumference = 2 * Math.PI * 16;
              const strokeDasharray = `${circumference} ${circumference}`;
              const strokeDashoffset =
                circumference - (phaseIndex / 7) * circumference;

              return (
                <div className="flex items-center gap-3">
                  <div className="relative w-10 h-10">
                    <svg className="w-full h-full" viewBox="0 0 40 40">
                      <circle
                        className="stroke-gray-200"
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        strokeWidth="3"
                      />
                      <circle
                        className="stroke-gray-700"
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        strokeWidth="3"
                        strokeDasharray={strokeDasharray}
                        strokeDashoffset={strokeDashoffset}
                        transform="rotate(-90 20 20)"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                      {phaseIndex}/7
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-gray-50 text-gray-700 border-gray-200"
                  >
                    {phase}
                  </Badge>
                </div>
              );
            }
          }
        ]
      : []),
    // Status column
    ...(isApplicationList
      ? [
          {
            id: "status",
            header: t("columns.status"),
            accessorFn: (row: ApplicationOrProfile) =>
              isApplicationList
                ? getStatusLabel((row as Application).status, t)
                : "N/A",
            enableSorting: true,
            cell: ({ row }: { row: Row<ApplicationOrProfile> }) => {
              if (!isApplicationList) return "-";
              const status = (row.original as Application).status;
              const statusLabel = getStatusLabel(status, t);
              const statusColors = {
                invited_to_interview:
                  "bg-yellow-50 text-yellow-700 border-yellow-200",
                passed_interview: "bg-green-50 text-green-700 border-green-200",
                failed_interview: "bg-red-50 text-red-700 border-red-200",
                declined: "bg-red-50 text-red-700 border-red-200"
              };

              return (
                <Badge
                  variant="outline"
                  className={statusColors[status as keyof typeof statusColors]}
                >
                  {statusLabel}
                </Badge>
              );
            }
          }
        ]
      : []),
    {
      accessorKey: "german_language_skills",
      header: t("columns.german_language_skills"),
      enableSorting: true,
      cell: ({ row }) => {
        const languageSkills = row.getValue("german_language_skills") as string;
        const {
          icon: Icon,
          color,
          tooltip
        } = getLanguageLevelIcon(languageSkills);
        return (
          <div className="flex items-center gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Icon className={`h-4 w-4 ${color}`} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="uppercase">{languageSkills || "-"}</span>
          </div>
        );
      }
    },
    {
      accessorKey: "nationality",
      header: t("columns.nationality"),
      cell: ({ row }) => {
        const nationality = row.getValue("nationality") as string;
        const countryOption = COUNTRY_OPTIONS.find(
          (o: any) => o.value.toLowerCase() === nationality?.toLowerCase()
        );
        return (
          <Badge variant="outline">
            {countryOption?.label || nationality || "-"}
          </Badge>
        );
      }
    },
    {
      accessorKey: "archived",
      header: t("common:status.archived"),
      cell: ({ row }) => {
        const archived = row.getValue("archived") as boolean;
        return (
          <Badge
            variant="outline"
            className={cn(
              archived
                ? "bg-red-50 text-red-700 border-red-200"
                : "bg-green-50 text-green-700 border-green-200"
            )}
          >
            {archived ? t("common:status.archived") : t("common:status.active")}
          </Badge>
        );
      }
    },
    // Metadata/Hidden columns (simplified for now but preserving existence)
    { accessorKey: "gender", header: t("columns.gender"), enableHiding: true },
    { accessorKey: "city", header: t("columns.city"), enableHiding: true },
    {
      accessorKey: "country",
      header: t("columns.country"),
      enableHiding: true
    },
    // Actions column
    {
      id: "actions",
      cell: ({ row }) => {
        const id = isApplicationList
          ? (row.original as Application).talent_application
          : (row.original as Profile).talent_id;
        if (!canShowActions) return null;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEditTalent(row);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                {t("common:actions.edit_talent")}
              </DropdownMenuItem>
              {canImpersonateProfiles && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onImpersonate((row.original as Profile).talent_id);
                  }}
                >
                  <UserCheck className="mr-2 h-4 w-4" />
                  {t("common:actions.impersonate")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(id!);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("actions.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    }
  ];
};
